import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";

const EMPTY = {
  project_name: "", location: "", need_identified_through: "Community Needs Assessment",
  supporter_name: "", supporter_role: "", amount_value: "", amount_numeric: 0, currency: "NGN",
  implemented_by: "Dutsen Kauna Foundation", beneficiaries: "", status: "planned",
  completion_report_url: "", impact_report_url: "", acknowledgement_text: "", image_url: "",
  is_public: true, order: 0,
};

const STATUSES = ["planned", "funded", "in_progress", "completed"];

export default function AdminSupportedProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => { const r = await api.get("/admin/supported-projects"); setItems(r.data); };
  useEffect(() => { document.title = "Admin · Supported Projects"; load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) { await api.put(`/admin/supported-projects/${editing.id}`, editing); toast.success("Updated"); }
      else { await api.post("/admin/supported-projects", editing); toast.success("Created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/supported-projects/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-supported">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold">Supported projects</h1>
          <p className="text-[#0a0d12]/60 text-sm">Public transparency record shown on the Governance page.</p>
        </div>
        <button onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="dk-btn-primary !py-2.5 !px-4 text-sm" data-testid="add-sp-btn">
          <Plus size={16} /> Add project
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-black/5 p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{p.project_name}</h3>
              <span className="px-2 py-0.5 rounded text-xs capitalize bg-[#f8f4e8]">{p.status.replace(/_/g, " ")}</span>
            </div>
            <div className="text-xs text-[#0a0d12]/60 mb-2">{p.location} · Supported by {p.supporter_name || "—"}</div>
            <div className="text-sm text-[#0a0d12]/80 mb-3">{p.amount_value || (p.amount_numeric ? `${p.currency} ${Number(p.amount_numeric).toLocaleString()}` : "—")}</div>
            <div className="flex gap-1">
              <button onClick={() => { setEditing(p); setOpen(true); }} className="p-1.5 hover:bg-[#f8f4e8] rounded"><Edit2 size={14} /></button>
              <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#0a0d12]/50">No supported projects yet.</p>}
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-7 max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl font-bold">{editing.id ? "Edit" : "Add"} supported project</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <FF label="Project name" required v={editing.project_name} on={(v) => setEditing({ ...editing, project_name: v })} className="sm:col-span-2" />
              <FF label="Location (Community, LGA, State)" v={editing.location} on={(v) => setEditing({ ...editing, location: v })} className="sm:col-span-2" />
              <FF label="Need identified through" v={editing.need_identified_through} on={(v) => setEditing({ ...editing, need_identified_through: v })} />
              <FF label="Supporter name / office" required v={editing.supporter_name} on={(v) => setEditing({ ...editing, supporter_name: v })} />
              <FF label="Supporter role" v={editing.supporter_role} on={(v) => setEditing({ ...editing, supporter_role: v })} />
              <FF label="Amount / value (display)" v={editing.amount_value} on={(v) => setEditing({ ...editing, amount_value: v })} />
              <FF label="Amount numeric" type="number" v={editing.amount_numeric} on={(v) => setEditing({ ...editing, amount_numeric: Number(v) })} />
              <FF label="Currency" v={editing.currency} on={(v) => setEditing({ ...editing, currency: v })} />
              <FF label="Beneficiaries" v={editing.beneficiaries} on={(v) => setEditing({ ...editing, beneficiaries: v })} className="sm:col-span-2" />
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <FF label="Order" type="number" v={editing.order} on={(v) => setEditing({ ...editing, order: Number(v) })} />
              <FF label="Completion report URL" v={editing.completion_report_url} on={(v) => setEditing({ ...editing, completion_report_url: v })} className="sm:col-span-2" />
              <FF label="Impact report URL" v={editing.impact_report_url} on={(v) => setEditing({ ...editing, impact_report_url: v })} className="sm:col-span-2" />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Acknowledgement text (public)</label>
                <textarea rows={2} value={editing.acknowledgement_text} onChange={(e) => setEditing({ ...editing, acknowledgement_text: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} /> Public
              </label>
            </div>
            <button type="submit" className="dk-btn-primary mt-6">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

function FF({ label, v, on, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
    </div>
  );
}
